import * as React from "react";
import * as ReactDOM from "react-dom";
import * as moment from "moment";
import * as modelsGet from "../../models/get";
import * as modelsCalendar from "../../models/calendar";
import { getEventColor, isEventFullyVisible, getEventPosition, getDeceasedPerson } from "../../utils/calendar";
import { lighten, darken } from "../../utils/color";
import { OptionalLink } from "../optionalLink";
import { Label, LabelColor } from "../label";

interface IProperties
{
    /**
     * Wenn angegeben, wird hiermit das zugrunde liegende DOM-Element ermittelt.
     */
    appointmentRef?: (ref: HTMLAnchorElement) => void,

    /**
     * Inline-Style.
     */
    style?: React.CSSProperties,

    /**
     * Der momentan selektierte Kalender.
     * Dieser Wert ist immer gefüllt auch wenn keine KalenderGruppe ausgewählt ist. Wenn keine
     * Gruppe ausgewählt ist, beinhaltet diese Eigenschaft den Kalender aus SelectedObject.
     */
    currentSelectedCalendar: modelsGet.ICalendar,

    /**
     * Der Name des verwendeten Termin Templates.
     */
    eventDayViewTemplate: string,

    /**
     * Gibt an, ob die Labels als Initialen gerendert werden sollen.
     */
    showLabelsAsInitials?: boolean,

    /**
     * Der Termin der gerendert werden soll.
     */
    appointment?: modelsGet.IAppointment,

    /**
     * Angabe, ob der Termin für die Monatsansicht gerendert werden soll.
     */
    isForMonthView: boolean,

    /**
     * Angabe, ob der Termin für die Planungsansicht gerendert werden soll.
     */
    isForPlanningView?: boolean,

    /**
     * Angabe, ob der Betreff fettgedruckt ist.
     */
    isSubjectBold?: boolean,

    /**
     * Angabe, ob der Termin gesperrt ist.
     */
    isDisabled?: boolean,

    /**
     * Wird ausgeführt, sobald auf das Item geklickt wird.
     */
    onClick?: (appointment: modelsGet.IAppointment) => void
}

export class Appointment extends React.Component<IProperties>
{
    /**
     * Standardwerte setzen.
     */
    public static defaultProps: Partial<IProperties> = {
        isSubjectBold: false,
        isDisabled: false
    };

    /**
     * Führt die "onClick" Eigenschaft aus wenn diese gesetzt wurde.
     * @param e Das auslösende Ereignis.
     */
    private onClick = (e: React.MouseEvent<HTMLElement>) =>
    {
        var { appointment, onClick } = this.props;
        onClick && onClick(appointment);
    }

    /**
     * Ermittelt den richtigen Betreff der angezeigt werden soll. Je nach eingerichtetem Template oder
     * Benutzerrecht, kann die Anzeige variieren.
     * @param isUserAuthorizedToSeeAppointment Angabe, ob der Benutzer den Termin einsehen darf.
     */
    private getSubject(isUserAuthorizedToSeeAppointment: boolean): string
    {
        var { appointment, eventDayViewTemplate } = this.props;

        if (isUserAuthorizedToSeeAppointment)
        {
            var text: string = "";

            if (eventDayViewTemplate === "EventType2")
            {
                if (appointment.persons != null)
                {
                    var deceased: modelsGet.IPerson = appointment.persons.where(p => p.is_deceased).first();
                    if (deceased != null)
                        return deceased.fullname_rev;
                    else
                        return appointment.subject;
                }
                else
                {
                    return appointment.subject;
                }
            }
            else
            {
                return appointment.calendar_name + ", " + appointment.subject;
            }
        }
        else
        {
            return appointment.calendar_name + ", Belegt";;
        }
    }

    /**
     * Gibt einen Wert zurück welcher angibt, ob der Betreff des Termins
     * als ein Detail Item des Termins gerendert werden kann.
     * @param isUserAuthorizedToSeeAppointment Angabe, ob der Benutzer den Termin einsehen darf.
     */
    private showSubjectDetailItem(isUserAuthorizedToSeeAppointment: boolean): boolean
    {
        var { appointment, eventDayViewTemplate } = this.props;

        if (isUserAuthorizedToSeeAppointment)
        {
            var text: string = "";

            if (eventDayViewTemplate === "EventType2")
            {
                var deceased: modelsGet.IPerson = getDeceasedPerson(appointment);
                if (deceased != null)
                    return true;
                else
                    return false;
            }
            else
            {
                return false;
            }
        }
        else
        {
            return false;
        }
    }

    /**
     * Gibt den Anzeige Text für das Betreff Detail Item zurück.
     * @param isUserAuthorizedToSeeAppointment Angabe, ob der Benutzer den Termin einsehen darf.
     */
    private getSubjectDetailItem(isUserAuthorizedToSeeAppointment: boolean): string
    {
        var { appointment, eventDayViewTemplate } = this.props;

        if (isUserAuthorizedToSeeAppointment)
        {
            var text: string = "";

            if (eventDayViewTemplate === "EventType2")
            {
                return appointment.subject + " " + appointment.burial_method_short;
            }
            else
            {
                return appointment.subject;
            }
        }
    }

    /**
     * Gibt den Anzeige Text für die Zeitangabe zurück (nur für das Detail Item).
     * @param isUserAuthorizedToSeeAppointment Angabe, ob der Benutzer den Termin einsehen darf.
     */
    private getTimeDetailItem(isUserAuthorizedToSeeAppointment: boolean): string
    {
        var { appointment } = this.props;

        var starttime: moment.Moment = moment(appointment.starttime);
        var endtime: moment.Moment = moment(appointment.endtime);
        var starttimeWithSt: moment.Moment = moment(appointment.starttime).subtract(appointment.setuptime_before, "minutes");
        var endtimeWithSt: moment.Moment = moment(appointment.endtime).add(appointment.setuptime_after, "minutes");

        if (appointment.setuptime_before > 0 || appointment.setuptime_after > 0)
        {
            if (isUserAuthorizedToSeeAppointment)
                return `${starttime.format("HH:mm")} - ${endtime.format("HH:mm")} (${starttimeWithSt.format("HH:mm")} - ${endtimeWithSt.format("HH:mm")})`;
            else
                return `${starttimeWithSt.format("HH:mm")} - ${endtimeWithSt.format("HH:mm")}`;
        }
        else
        {
            return `${starttime.format("HH:mm")} - ${endtime.format("HH:mm")}`;
        }
    }

    /**
     * Gibt alle Personen des Termins zurück.
     */
    private getPersons(): modelsGet.IPerson[]
    {
        var { appointment, eventDayViewTemplate } = this.props;

        if (appointment.persons != null && appointment.persons.length > 0)
        {
            if (eventDayViewTemplate === "EventType2")
            {
                return appointment.persons.where(p => !p.is_deceased);
            }
            else
            {
                return appointment.persons;
            }
        }
        else
        {
            return [];
        }
    }

    /**
     * Gibt den Anzeigetext für Labels die als Initialen gerendert werden sollen zurück.
     * @param labels
     */
    private getLabelInitials(labels: modelsGet.ILabel[]): string
    {
        var initials = labels.where(l => l.label_initial != null && l.label_initial !== "");
        if (initials.length > 0)
        {
            var initialsArray: any = initials.select(l => "[" + l.label_initial + "]");
            return initialsArray.join(" ");
        }
    }

    /**
     * Wenn die Labels nicht als Initialen gerendert werden sollen, werden hier die Labels
     * für die Anzeige richtig sortiert zurückgegeben.
     * @param labels Die Labeles des Termins.
     */
    public getLabels(labels: modelsGet.ILabel[]): modelsGet.ILabel[]
    {
        if (labels != null && labels.length > 0)
        {
            var sortedLabels: modelsGet.ILabel[] = [];
            for (var i = labels.length - 1; i >= 0; i--)
            {
                if (labels[i].label_color !== "null")
                    sortedLabels.push(labels[i]);
            }

            return sortedLabels;
        }
    }

    /**
     * Rendert die Labels des Termins.
     */
    private renderLabels(): JSX.Element
    {
        const { appointment, showLabelsAsInitials } = this.props;

        if (appointment != null && appointment.id > 0 && appointment.labels != null && appointment.labels.length > 0)
        {
            if (showLabelsAsInitials)
            {
                return (
                    <div className="event_initials">
                        {this.getLabelInitials(appointment.labels)}
                    </div>
                );
            }
            else
            {
                return (
                    <div className="event_labels">
                        {
                            this.getLabels(appointment.labels).map((label) =>
                            {
                                return (
                                    <Label color={label.label_color} />
                                );
                            })
                        }
                    </div>
                );
            }
        }

        return null;
    }

    /**
     * Rendert die Rüstzeiten des Termins.
     * @param before Angabe, ob die Rüstzeit vor dem Termin gerendert werden soll.
     * @param isUserAuthorizedToSeeAppointment Angabe, ob der Benutzer den Termin sehen darf.
     * @param style Style mit Positions und Höhen angaben.
     */
    private renderSetuptime(before: boolean, isUserAuthorizedToSeeAppointment: boolean, style: React.CSSProperties): JSX.Element
    {
        var { appointment, isForMonthView } = this.props;

        if (isUserAuthorizedToSeeAppointment && !isForMonthView && (before ? appointment.setuptime_before > 0 : appointment.setuptime_after > 0))
        {
            return (
                <div
                    style={style}
                    className="event_setuptime">
                    {(before ? appointment.setuptime_before : appointment.setuptime_after) + " Min. Rüstzeit"}
                </div>
            );
        }
        else
        {
            return null;
        }
    }

    /**
     * Render die Detail Anzeige des Termins.
     * @param isUserAuthorizedToSeeAppointment
     */
    private renderDetails(isUserAuthorizedToSeeAppointment: boolean): JSX.Element
    {
        var { appointment, isForMonthView } = this.props;
        var details: JSX.Element[] = [];

        if (isForMonthView)
            return null;

        if (isUserAuthorizedToSeeAppointment)
        {
            if (this.showSubjectDetailItem(isUserAuthorizedToSeeAppointment))
            {
                details.push(
                    <div className="event_content_detail">
                        <div className="event_content_detail_text">{this.getSubjectDetailItem(isUserAuthorizedToSeeAppointment)}</div>
                    </div>
                );
            }

            details.push(
                <div className="event_content_detail">
                    <div className="event_content_detail_text">{this.getTimeDetailItem(isUserAuthorizedToSeeAppointment)}</div>
                </div>
            );

            if (appointment.remark != null)
            {
                details.push(
                    <div className="event_content_detail">
                        <div className="event_content_detail_text">{appointment.remark}</div>
                    </div>
                );
            }

            var persons: modelsGet.IPerson[] = this.getPersons();
            if (persons != null && persons.length > 0)
            {
                for (let person of persons)
                {
                    details.push(
                        <div className="event_content_detail">
                            <div className="event_content_detail_type">{person.person_type.charAt(0) + ":"}</div>
                            <div className="event_content_detail_text">{person.fullname_rev}</div>
                        </div>
                    );
                }
            }

            if (appointment.burial_method != null)
            {
                details.push(
                    <div className="event_content_detail">
                        <div className="event_content_detail_text">{appointment.burial_method}</div>
                    </div>
                );
            }

            if (appointment.grave_no != null)
            {
                details.push(
                    <div className="event_content_detail">
                        <div className="event_content_detail_text">{appointment.grave_no}</div>
                    </div>
                );
            }

            if (appointment.grave_type != null)
            {
                details.push(
                    <div className="event_content_detail">
                        <div className="event_content_detail_text">{appointment.grave_type}</div>
                    </div>
                );
            }
        }
        else
        {
            details.push(
                <div className="event_content_detail">
                    <div className="event_content_detail_text">{this.getTimeDetailItem(isUserAuthorizedToSeeAppointment)}</div>
                </div>
            );
        }

        return (
            <div className="event_content_details">
                {details}
            </div>
        );
    }

    /**
     * Rendert die Komponente.
     */
    public render(): JSX.Element
    {
        var {
            appointmentRef,
            style,
            currentSelectedCalendar,
            eventDayViewTemplate,
            showLabelsAsInitials,
            appointment,
            isForMonthView,
            isForPlanningView,
            isSubjectBold,
            isDisabled
        } = this.props;

        if (appointment != null)
        {
            // CSS-Klasse generieren.
            var classNames: string[] = ["event"]

            if (isForMonthView)
                classNames.push("event--monthv");
            else
                classNames.push("event--dayv");

            var isUserAuthorizedToSeeAppointment: boolean = appointment.appointment_type !== "not_authorized";
            if (isUserAuthorizedToSeeAppointment)
            {
                if (appointment.display_type_id > 0)
                    classNames.push("event--" + appointment.display_type_pattern);
            }
            else
            {
                classNames.push("event--is-locked");
            }

            // Farbe des Termins ermitteln.
            var eventColor: string = getEventColor(appointment);

            // Styles generieren.
            let baseStyle: React.CSSProperties = {
                opacity: (isEventFullyVisible(appointment, currentSelectedCalendar.id) ? 1 : 0.4),
                ...style
            };

            let setuptimeBeforeStyle: React.CSSProperties = {
                color: darken(eventColor, 0.3),
                backgroundColor: lighten(eventColor, 0.7)
            };

            let setuptimeAfterStyle: React.CSSProperties = {
                ...setuptimeBeforeStyle
            };

            let contentStyle: React.CSSProperties = {
                ...setuptimeBeforeStyle
            };

            // Position des Termins ermitteln.
            if (!isForMonthView)
            {
                let eventPosition = getEventPosition(appointment);
                baseStyle.top = eventPosition.base.top;

                if (isUserAuthorizedToSeeAppointment)
                {
                    setuptimeBeforeStyle.height = eventPosition.setuptimeBefore.height;
                    setuptimeAfterStyle.height = eventPosition.setuptimeAfter.height;
                    contentStyle.height = eventPosition.content.height;
                    contentStyle.padding = eventPosition.content.padding;
                }
                else
                {
                    contentStyle.height = (eventPosition.setuptimeBefore.height as number) + (eventPosition.content.height as number) + (eventPosition.setuptimeAfter.height as number);
                    contentStyle.padding = eventPosition.content.padding;
                }
            }

            let linkTo: Object = {
                pathname: `/event/${appointment.id}${isForPlanningView ? "?calendar=" + currentSelectedCalendar.id : ""}`,
                state: {
                    modal: true
                }
            }

            if (!isUserAuthorizedToSeeAppointment)
                linkTo = null;
            else if (isDisabled)
                linkTo = null;

            return (
                <OptionalLink
                    anchorRef={appointmentRef}
                    className={classNames.join(" ")}
                    style={baseStyle}
                    onClick={this.onClick}
                    to={linkTo}>
                    <div
                        className="event_border"
                        style={{
                            backgroundColor: eventColor,
                            cursor: (isUserAuthorizedToSeeAppointment ? "" : "default")
                        }}></div>
                    {this.renderSetuptime(true, isUserAuthorizedToSeeAppointment, setuptimeBeforeStyle)}
                    <div
                        className="event_content"
                        style={contentStyle}>
                        {!isForMonthView && showLabelsAsInitials && this.renderLabels()}
                        <div className="event_content_subject">
                            {
                                appointment.sync_person_id > 0 &&
                                <div className="event_content_subject_copiedtoapp">
                                    <i className="mdi mdi-check-all"></i>
                                </div>
                            }
                            {isForMonthView && <div className="event_content_subject_time">{moment(appointment.starttime).format("HH:mm")}</div>}
                            <div className={`event_content_subject_text ${isSubjectBold ? "event_content_subject--is-bold" : ""}`}>{this.getSubject(isUserAuthorizedToSeeAppointment)}</div>
                        </div>
                        {this.renderDetails(isUserAuthorizedToSeeAppointment)}
                    </div>
                    {this.renderSetuptime(false, isUserAuthorizedToSeeAppointment, setuptimeAfterStyle)}
                </OptionalLink>
            );
        }
        else
        {
            return null;
        }
    }
}